import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AbstractService } from '../commons/abstract.service';
import { Pager } from '../commons/pager';
import { Task, TaskTodayGroup } from '../models/task.model';
import { User } from '../models/user.model';

export interface OrderedTask {
  column: string;
  tasks: Array<Task>;
  totalCount: number;
}

/** The filters every activity reading accepts (E4 RF-39, RF-44); values are sent as they are. */
export type TaskReadingParams = Record<string, string | number | boolean>;

/** Response of `GET /api/task/today` (E4 RF-36 to RF-38). */
export interface TaskTodayReading {
  timezone: string;
  now: string;
  dayStart: string;
  dayEnd: string;
  responsible: string | null;
  groups: Record<TaskTodayGroup, Array<Task>>;
  progress: { scheduled: number; concluded: number };
  next: { task: string; dueAt: string; user: Pick<User, '_id' | 'name' | 'image'> | null; name?: string; type?: string } | null;
}

/** Response of `GET /api/task/agenda` (E4 RF-42, RF-43): flat rows, each with its company-local `day`. */
export interface TaskAgendaReading {
  timezone: string;
  from: string;
  to: string;
  responsible: string | null;
  activities: Array<Task>;
  overdueCount: number;
}

/** The streak as the API answers it (E4 RF-47): `capped` means the 120-day window was reached — shown as "N+". */
export interface TaskStreak {
  days: number;
  capped: boolean;
}

/** Response of `GET /api/task/dashboard` (E4 RF-46, RF-47). */
export interface TaskDashboardReading {
  timezone: string;
  from: string;
  to: string;
  today: { scheduled: number; concluded: number };
  /** Every open activity that is not overdue, one with no date included. */
  pending: number;
  /** Of `pending`, those with no date: they appear in no day view until they get one. */
  undated: number;
  overdue: number;
  concludedThisWeek: number;
  skippedNextSteps: number;
  concludedPerDay: Array<{ day: string; count: number }>;
  streak: TaskStreak;
}

/** One row of `GET /api/task/discipline` (E4 RF-48). */
export interface TaskDisciplineRow {
  user: { _id: string; name: string | null; image: string | null };
  scheduled: number;
  concluded: number;
  overdue: number;
  completionRate: number | null;
  postponements: number;
  postponementRate: number | null;
  skippedNextSteps: number;
  openDeals: number;
  openDealsWithoutNextAction: number;
  streak: TaskStreak;
  averageConclusionsPerActiveDay: number | null;
  averageDurationMinutes: number | null;
}

export interface TaskDisciplineReading {
  timezone: string;
  from: string;
  to: string;
  rows: Array<TaskDisciplineRow>;
}

/** Body of `POST /api/task/:idTask/postpone` (E4 RF-24). */
export interface TaskPostponePayload {
  dueAt: string;
  untimed?: boolean;
  reason: string;
}

@Injectable({ providedIn: 'root' })
export class TasksService extends AbstractService<Task> {
  getURLBase(): string {
    return '/task';
  }

  /** A deal's activities, most recent first (RF-38). */
  listByCard(idCard: string): Observable<Array<Task>> {
    return super.get(`/card/${idCard}`);
  }

  // ---- E4 (SPEC-2026-10-crm-activities-cadence) ----

  /** The Today view (RF-36 to RF-39, RF-44). */
  today(params: TaskReadingParams = {}): Observable<TaskTodayReading> {
    return super.getWithParams('/today', params);
  }

  /** The week, month and calendar readings (RF-42, RF-43): `from` / `to` as `yyyy-MM-dd`. */
  agenda(params: TaskReadingParams): Observable<TaskAgendaReading> {
    return super.getWithParams('/agenda', params);
  }

  /** Postpones an open activity to a later moment, with a reason (RF-24 to RF-27). */
  postpone(idTask: string, payload: TaskPostponePayload): Observable<Task> {
    return super.post(`/${idTask}/postpone`, payload);
  }

  /** The next working day at 09:00 in the company's zone — the default of the postpone dialog (RF-25). */
  nextWorkingMoment(): Observable<{ dueAt: string }> {
    return super.get('/next-working-moment');
  }

  /** The requester's own dashboard and streak (RF-46, RF-47). */
  dashboard(params: TaskReadingParams = {}): Observable<TaskDashboardReading> {
    return super.getWithParams('/dashboard', params);
  }

  /** The team's discipline — supervisors and administrators only (RF-48, RF-49). */
  discipline(params: TaskReadingParams = {}): Observable<TaskDisciplineReading> {
    return super.getWithParams('/discipline', params);
  }

  constructor(private http: HttpClient) {
    super(http);
  }

  override preCreate(obj: Task): void {

  }

  preUpdate(obj: Task): void {
  }

  getTasksByDate(page: number, perPage: number, search?: string, params?: Record<string, any>): Observable<Pager<Task>> {
    const baseURL = `/ordered-list`;
    const pager = `${baseURL}?page=${page}&perPage=${perPage}`;

    const getSearchConfig = (search: string): string => {
      if (!!search) {
        return '&search=' + encodeURIComponent(search);
      } else {
        return '';
      }
    }
    const searching = `${pager}${getSearchConfig(search)}`;

    let urling = `${searching}`;
    if (!!params) {
      for (let param in params) {
        urling = `${urling}&${param}=${params[param]}`;
      }
    }
    return super.get(urling);
  }

  startTask(taskId: string): Observable<Task> {
    return super.get(`/start/${taskId}`);
  }

  completeTask(taskId: string): Observable<Task> {
    return super.get(`/complete/${taskId}`);
  }
}
